import type { TableColumnsType } from 'antd';
import { Button, Table, Tag } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatISODateTime, formatTaskDateRangeToISO } from 'utils/dateTimeUtils';
import TablePaginationBar from '../../../components/TablePaginationBar';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import FilterArea, { ClientCartonFilterOptions, DEFAULT_FILTER_OPTIONS } from './components/FilterArea';
import ClientCartonDetailDrawer from './components/ClientCartonDetailDrawer';
import { useClientCartonQuery } from './hooks/useClientCartonQuery';
import { GetClientCartonsVariables } from './graphql/queries/getClientCartons';
import { ClientCartonListItem } from './graphql/types/ClientCartonTypes';

const ClientCartonsPage: React.FC = () => {
    const { currentWarehouse } = useWarehouseContext();
    const { t } = useTranslation();
    const [filterOptions, setFilterOptions] = useState<ClientCartonFilterOptions>(DEFAULT_FILTER_OPTIONS);
    const [tempFilterOptions, setTempFilterOptions] = useState<ClientCartonFilterOptions>(DEFAULT_FILTER_OPTIONS);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedCartonNo, setSelectedCartonNo] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<number | null>(null);

    const buildVariables = (filters: ClientCartonFilterOptions, page: number = currentPage, size: number = pageSize): GetClientCartonsVariables => {
        const vars: GetClientCartonsVariables = {
            pageRequest: {
                page,
                size,
            },
        };
        if (currentWarehouse?.id && currentWarehouse.id !== '0') {
            vars.hubFilterWarehouseId = Number(currentWarehouse.id);
        }
        if (filters.bigBagNo) vars.bigBagNo = filters.bigBagNo;
        if (filters.masterWaybillNo) vars.masterWaybillNo = filters.masterWaybillNo;
        if (filters.channelId !== 'all') vars.channelId = Number(filters.channelId);
        if (filters.arrivePortCode !== 'all') vars.arrivePortCode = filters.arrivePortCode;
        if (filters.scanWarehouseId !== 'all') vars.scanWarehouseId = Number(filters.scanWarehouseId);
        if (filters.createdAtRange[0] && filters.createdAtRange[1]) {
            const range = formatTaskDateRangeToISO([
                filters.createdAtRange[0].format('YYYY/MM/DD'),
                filters.createdAtRange[1].format('YYYY/MM/DD'),
            ]);
            if (range) {
                vars.createdAtFrom = range.start;
                vars.createdAtTo = range.end;
            }
        }
        if (filters.scanTimeRange[0] && filters.scanTimeRange[1]) {
            const range = formatTaskDateRangeToISO([
                filters.scanTimeRange[0].format('YYYY/MM/DD'),
                filters.scanTimeRange[1].format('YYYY/MM/DD'),
            ]);
            if (range) {
                vars.scanTimeFrom = range.start;
                vars.scanTimeTo = range.end;
            }
        }
        return vars;
    };

    const { items, totalCount, loading, refetch } = useClientCartonQuery(
        buildVariables(filterOptions),
        !currentWarehouse
    );

    const handleFilter = () => {
        setFilterOptions({ ...tempFilterOptions });
        setCurrentPage(1);
        refetch(buildVariables(tempFilterOptions, 1, pageSize));
    };

    const handleReset = () => {
        setTempFilterOptions(DEFAULT_FILTER_OPTIONS);
        setFilterOptions(DEFAULT_FILTER_OPTIONS);
        setCurrentPage(1);
    };

    const columns: TableColumnsType<ClientCartonListItem> = [
        {
            title: t('wms.clientCartons.columns.cartonNo'),
            dataIndex: 'bigBagNo',
            key: 'bigBagNo',
            render: (text: string, record: ClientCartonListItem) => (
                <a
                    className="text-blue-600 cursor-pointer"
                    onClick={() => {
                        setSelectedCartonNo(text);
                        setSelectedId(record.id);
                        setDrawerVisible(true);
                    }}
                >
                    {text}
                </a>
            ),
        },
        {
            title: t('wms.clientCartons.columns.masterWaybillNo'),
            dataIndex: 'masterWaybillNo',
            key: 'masterWaybillNo',
            render: (v: string | null) => v || '-',
        },
        {
            title: t('wms.clientCartons.columns.customerName'),
            dataIndex: 'channelName',
            key: 'channelName',
            render: (v: string | null) => v || '-',
        },
        {
            title: t('wms.clientCartons.columns.destinationPort'),
            dataIndex: 'arrivePortCode',
            key: 'arrivePortCode',
            render: (v: string | null) => v || '-',
        },
        {
            title: t('wms.clientCartons.columns.presortCode'),
            dataIndex: 'presortCode',
            key: 'presortCode',
            render: (v: string | null) => v || '-',
        },
        {
            title: t('wms.clientCartons.columns.status'),
            dataIndex: 'scanStatus',
            key: 'scanStatus',
            render: (status: number) => (
                <Tag color={status === 1 ? 'green' : 'default'}>
                    {status === 1
                        ? t('wms.clientCartons.status.scanned')
                        : t('wms.clientCartons.status.notScanned')}
                </Tag>
            ),
        },
        {
            title: t('wms.clientCartons.columns.scanType'),
            dataIndex: 'scanType',
            key: 'scanType',
            render: (v: number | null) => {
                if (v === 1) return t('wms.clientCartons.scanType.batch');
                if (v === 2) return t('wms.clientCartons.scanType.individual');
                return '-';
            },
        },
        {
            title: t('wms.clientCartons.columns.scanPort'),
            dataIndex: 'scanPort',
            key: 'scanPort',
            render: (v: string | null) => v || '-',
        },
        {
            title: t('wms.clientCartons.columns.parcelCount'),
            dataIndex: 'parcelCount',
            key: 'parcelCount',
        },
        {
            title: t('wms.clientCartons.columns.onlineParcelCount'),
            dataIndex: 'onlineParcelCount',
            key: 'onlineParcelCount',
        },
        {
            title: t('wms.clientCartons.columns.offlineParcelCount'),
            dataIndex: 'offlineParcelCount',
            key: 'offlineParcelCount',
        },
        {
            title: t('wms.clientCartons.columns.createdTime'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (v: string | null) => (v ? formatISODateTime(v) : '-'),
        },
        {
            title: t('wms.clientCartons.columns.cartonScanTime'),
            dataIndex: 'scanTime',
            key: 'scanTime',
            render: (v: string | null) => (v ? formatISODateTime(v) : '-'),
        },
    ];

    return (
        <div className="flex flex-col h-full">
            <FilterArea filters={tempFilterOptions} setFilters={setTempFilterOptions} />

            <div className="flex justify-end items-center mb-3 gap-2">
                <Button type="primary" onClick={handleFilter}>
                    {t('wms.clientCartons.filters.filter')}
                </Button>
                <Button onClick={handleReset}>
                    {t('wms.clientCartons.filters.reset')}
                </Button>
            </div>

            <div className="flex-1 overflow-auto">
                <Table
                    rowKey="bigBagNo"
                    columns={columns}
                    dataSource={items}
                    loading={loading}
                    pagination={false}
                    size="small"
                    scroll={{ x: 'max-content' }}
                />
            </div>

            <TablePaginationBar
                total={totalCount}
                current={currentPage}
                pageSize={pageSize}
                onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                }}
                showSizeChanger
            />

            <ClientCartonDetailDrawer
                visible={drawerVisible}
                clientCartonNo={selectedCartonNo}
                id={selectedId}
                onClose={() => setDrawerVisible(false)}
            />
        </div>
    );
};

export default ClientCartonsPage;
